import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  FormFieldType,
  type ChecklistResponse,
  type FormAnswer,
  type SubmitChecklistRequest,
} from '@troia-v3/types';

import {
  Breadcrumb,
  Button,
  ListPageLoading,
  PageToolbar,
} from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useAuth } from '@/contexts/AuthContext';
import { useUnitsDropdown } from '@/shared/hooks/useUnitsDropdown';
import { FillField, LAYOUT_TYPES, isEmptyAnswer, type AnswerValue } from '@/shared/components/forms';

import { checklistsApi } from '../services/checklistsApi';
import { useChecklist, useSubmitChecklist } from '../hooks/useChecklists';
import { formatCountdown } from './MyChecklistsPage';

/** Status em que o assignee ainda pode preencher/enviar. */
const OPEN_STATUSES: ReadonlySet<ChecklistResponse['status']> = new Set(['pending', 'in_progress']);

/** Teto de espera pelo GPS antes de seguir sem ele (spec §7). */
const GPS_TIMEOUT_MS = 8000;

type GpsPayload = NonNullable<SubmitChecklistRequest['gps']>;

/**
 * Lê a posição atual com teto duplo de 8s: a opção `timeout` do próprio
 * `getCurrentPosition` E um `setTimeout` externo. O segundo existe porque em
 * várias implementações o relógio da opção só começa DEPOIS que o usuário
 * responde o prompt de permissão — sem ele, um prompt ignorado deixaria o botão
 * "Enviar" girando para sempre. Nunca rejeita: a ausência de GPS não pode
 * impedir o envio (spec §7).
 */
const readGpsPosition = async (): Promise<GpsPayload | undefined> => {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return undefined;

  return new Promise<GpsPayload | undefined>((resolve) => {
    let settled = false;
    const finish = (value: GpsPayload | undefined): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      resolve(value);
    };
    const timer = window.setTimeout(() => finish(undefined), GPS_TIMEOUT_MS);

    navigator.geolocation.getCurrentPosition(
      (position) =>
        finish({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        }),
      () => finish(undefined), // negado, indisponível ou timeout do próprio browser
      { enableHighAccuracy: true, timeout: GPS_TIMEOUT_MS, maximumAge: 0 },
    );
  });
};

/**
 * ChecklistFillPage — o preenchimento no campo.
 *
 * Fluxo: `getById` → (se `pending`) `start` → render de `templateSnapshot.fields`
 * na ordem do snapshot → validação client-side de obrigatórios → GPS opcional →
 * `submit` → volta para "Meus checklists".
 *
 * A tela é desenhada para o caminho feliz em menos de 5 minutos (spec §7):
 * conformidade e foto — os dois tipos que dominam um checklist real — são
 * controles de UM toque, e o botão de envio é uma barra fixa no rodapé que não
 * exige rolar de volta ao topo.
 *
 * O snapshot é a fonte da verdade do formulário: o modelo pode ter sido
 * renomeado, alterado ou excluído desde a criação da instância, e o backend
 * reconcilia as answers contra esse mesmo snapshot no `submit` (fieldId
 * desconhecido é descartado, `fieldType`/`fieldLabel` são reescritos a partir
 * dele). Por isso o client só precisa acertar `fieldId` + `value`.
 */
export const ChecklistFillPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { state } = useAuth();
  const currentUserId = state.user?.id;

  const { data: checklist, isLoading, error } = useChecklist(id);
  const { data: unitOptions } = useUnitsDropdown();
  const submitMutation = useSubmitChecklist();

  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  /** Uploads de foto em voo, por `fieldId` (reportado pelo `PhotoCaptureField`). */
  const [pendingPhotos, setPendingPhotos] = useState<Record<string, number>>({});

  const startedRef = useRef(false);
  const hydratedRef = useRef(false);
  // Depois do `submit` o status sai de `in_progress` e o guard abaixo mandaria
  // o usuário para a revisão — mas o destino correto é "Meus checklists".
  const submittedRef = useRef(false);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => window.clearInterval(timer);
  }, []);

  const isAssignee = !!currentUserId && checklist?.assigneeId === currentUserId;
  const isOpen = !!checklist && OPEN_STATUSES.has(checklist.status);

  /**
   * Guard. Seguidor (que também recebe o checklist em `/my`) e checklist já
   * enviado/aprovado/expirado caem na revisão read-only — `start`/`submit` são
   * assignee-only no backend, então preencher aqui seria um beco sem saída.
   */
  useEffect(() => {
    if (!checklist || !id || submittedRef.current) return;
    if (!isAssignee || !isOpen) navigate(`/checklists/${id}`, { replace: true });
  }, [checklist, id, isAssignee, isOpen, navigate]);

  /**
   * `pending` → `in_progress`, uma única vez (`startedRef`).
   *
   * Só disparamos a partir de `pending`: o backend responde 409 quando o
   * checklist já está `in_progress`, e o caminho normal de reentrada (voltar
   * para a tela depois de sair no meio) já chega com `in_progress` do `getById`.
   * Um 409 residual (duas abas abertas na mesma instância) é benigno — a
   * transição desejada já aconteceu — e é engolido aqui para não bloquear o
   * preenchimento.
   *
   * Chamada direta no `checklistsApi` em vez de `useStartChecklist`: o hook
   * dispara um `toast.error` no `onError`, o que transformaria essa corrida
   * benigna em erro visível para o gerente. `_handleErrorLocally: true` cala
   * também o toast do interceptor global de `@/lib/api` — os dois canais de
   * erro precisam ser silenciados para o 409 ficar realmente invisível.
   */
  useEffect(() => {
    if (!checklist || !id || startedRef.current) return;
    if (!isAssignee || checklist.status !== 'pending') return;

    startedRef.current = true;
    void (async () => {
      try {
        await checklistsApi.start(id, { _handleErrorLocally: true });
      } catch {
        // 409 (já in_progress) ou falha transitória: o preenchimento continua
        // válido — o `submit` é quem realmente decide o estado final.
      } finally {
        void queryClient.invalidateQueries({ queryKey: ['checklist', id] });
      }
    })();
  }, [checklist, id, isAssignee, queryClient]);

  /**
   * Hidrata as respostas já gravadas — importante no ciclo
   * "reprovado → reabrir → corrigir": o gerente ajusta o que o revisor apontou
   * em vez de refazer o checklist inteiro.
   */
  useEffect(() => {
    if (!checklist || hydratedRef.current) return;
    hydratedRef.current = true;
    const initial: Record<string, AnswerValue> = {};
    for (const answer of checklist.answers ?? []) initial[answer.fieldId] = answer.value;
    setAnswers(initial);
  }, [checklist]);

  const fields = useMemo(
    () => [...(checklist?.templateSnapshot.fields ?? [])].sort((a, b) => a.order - b.order),
    [checklist],
  );

  const setAnswer = useCallback((fieldId: string, value: AnswerValue): void => {
    setAnswers((prev) => ({ ...prev, [fieldId]: value }));
    setFieldErrors((prev) => {
      if (!prev[fieldId]) return prev;
      const next = { ...prev };
      delete next[fieldId];
      return next;
    });
  }, []);

  const setPendingForField = useCallback((fieldId: string, count: number): void => {
    setPendingPhotos((prev) => (prev[fieldId] === count ? prev : { ...prev, [fieldId]: count }));
  }, []);

  const uploadsInFlight = useMemo(
    () => Object.values(pendingPhotos).reduce((sum, count) => sum + count, 0),
    [pendingPhotos],
  );

  const handleSubmit = async (): Promise<void> => {
    if (!checklist || !id) return;

    // Validação client-side dos obrigatórios (o backend revalida e devolve 422).
    const errors: Record<string, string> = {};
    for (const field of fields) {
      if (LAYOUT_TYPES.has(field.type) || !field.required) continue;
      if (!isEmptyAnswer(answers[field.id])) continue;

      if (field.type === FormFieldType.PHOTO) {
        // Foto obrigatória ainda vazia PORQUE o upload não terminou não é o
        // mesmo erro que foto obrigatória esquecida — a instrução muda.
        errors[field.id] = (pendingPhotos[field.id] ?? 0) > 0 ? 'Foto em envio — aguarde' : 'Envie pelo menos uma foto';
      } else {
        errors[field.id] = 'Este campo é obrigatório';
      }
    }

    const scrollToFirstError = (): void => {
      const firstId = fields.find((field) => errors[field.id])?.id;
      if (firstId) {
        document.getElementById(`checklist-field-${firstId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    /*
     * Upload em voo bloqueia o envio. Sem isso, a foto tirada segundos antes do
     * toque em "Enviar" ainda não está em `answers` e o checklist iria embora
     * sem ela — com toast de SUCESSO, que é o pior desfecho possível: ninguém
     * descobre até a revisão.
     */
    if (uploadsInFlight > 0) {
      setFieldErrors(errors);
      scrollToFirstError();
      toast.error(
        uploadsInFlight === 1
          ? 'Aguarde o envio da foto antes de enviar o checklist.'
          : `Aguarde o envio das ${uploadsInFlight} fotos antes de enviar o checklist.`,
      );
      return;
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      scrollToFirstError();
      toast.error('Preencha os campos obrigatórios destacados.');
      return;
    }

    setSubmitting(true);
    try {
      // `fieldType`/`fieldLabel` são reescritos pelo backend a partir do
      // snapshot — mandamos os do snapshot para manter o payload coerente.
      const payloadAnswers: FormAnswer[] = fields
        .filter((field) => !LAYOUT_TYPES.has(field.type))
        .map((field) => {
          const raw = answers[field.id];
          return {
            fieldId: field.id,
            fieldType: field.type,
            fieldLabel: field.label,
            value: raw === undefined ? (field.type === FormFieldType.PHOTO ? [] : null) : raw,
          };
        });

      const gps = checklist.templateSnapshot.checklistSettings?.captureGps ? await readGpsPosition() : undefined;
      if (checklist.templateSnapshot.checklistSettings?.captureGps && !gps) {
        toast.info('Não foi possível obter a localização — o checklist será enviado sem GPS.');
      }

      const payload: SubmitChecklistRequest = gps ? { answers: payloadAnswers, gps } : { answers: payloadAnswers };
      submittedRef.current = true;
      await submitMutation.mutateAsync({ id, data: payload });
      navigate('/checklists/my');
    } catch {
      // Mensagem já exibida pelo interceptor global + `onError` do hook. O
      // checklist continua `in_progress`: reabilita o guard e deixa o gerente
      // tentar de novo sem perder nada do que já preencheu.
      submittedRef.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading || !id) {
    return (
      <div className="h-full flex items-center justify-center" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        <ListPageLoading message="Carregando checklist..." />
      </div>
    );
  }

  if (error || !checklist) {
    return (
      <div className="h-full flex items-center justify-center" style={{ backgroundColor: 'var(--theme-page-background)' }}>
        <div className="text-center px-4">
          <p style={{ color: 'var(--theme-error-color)' }} className="mb-3">
            {(error as Error | undefined)?.message ?? 'Checklist não encontrado'}
          </p>
          <Button variant="secondary" onClick={() => navigate('/checklists/my')} data-testid="checklist-fill-error-back">
            Voltar para Meus checklists
          </Button>
        </div>
      </div>
    );
  }

  // O guard já disparou o `navigate` — evita renderizar o formulário por um frame.
  if (!isAssignee || !isOpen) return null;

  const unitName = checklist.unitId
    ? (unitOptions ?? []).find((unit) => unit.value === checklist.unitId)?.label
    : undefined;
  const countdown = formatCountdown(checklist.expiresAt, now);
  const capturesGps = checklist.templateSnapshot.checklistSettings?.captureGps === true;

  return (
    <div
      className="h-full flex flex-col"
      style={{ backgroundColor: 'var(--theme-page-background)' }}
      data-testid="checklist-fill-page"
    >
      <PageToolbar
        data-testid="checklist-fill-toolbar"
        left={
          <Breadcrumb
            items={[
              { label: 'Meus checklists', onNavigate: () => navigate('/checklists/my'), testid: 'checklist-fill-breadcrumb-list' },
              { label: checklist.templateSnapshot.name, testid: 'checklist-fill-breadcrumb-current' },
            ]}
            mobileLegend="Meus checklists"
            data-testid="checklist-fill-breadcrumb"
          />
        }
      />

      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-4 sm:py-6">
          <div
            className="rounded-xl p-4 mb-3"
            style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
            data-testid="checklist-fill-header"
          >
            <h1 className="text-lg font-bold leading-snug" style={{ color: 'var(--theme-card-text)' }}>
              {checklist.templateSnapshot.name}
            </h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[13px]">
              <span className="inline-flex items-center gap-1.5" style={{ color: 'var(--theme-muted-text)' }}>
                <Icon name="map-pin" className="w-4 h-4" />
                {unitName ?? 'Sem unidade'}
              </span>
              <span
                className="inline-flex items-center gap-1.5 font-semibold"
                style={{ color: countdown.tone === 'urgent' ? 'var(--theme-error-fg)' : 'var(--theme-muted-text)' }}
                data-testid="checklist-fill-countdown"
              >
                <Icon name="clock" className="w-4 h-4" />
                {countdown.label}
              </span>
              {capturesGps && (
                <span className="inline-flex items-center gap-1.5" style={{ color: 'var(--theme-muted-text)' }}>
                  <Icon name="globe" className="w-4 h-4" />
                  Localização registrada no envio
                </span>
              )}
            </div>
          </div>

          <div
            className="rounded-xl p-4 sm:p-5"
            style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
            data-testid="checklist-fill-fields"
          >
            {fields.map((field) => (
              <FillField
                key={field.id}
                field={field}
                value={answers[field.id]}
                error={fieldErrors[field.id]}
                onChange={(value) => setAnswer(field.id, value)}
                onPendingChange={(count) => setPendingForField(field.id, count)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Barra de envio fixa: o gerente termina a última foto e envia sem rolar de volta. */}
      <div
        className="flex-none px-4 sm:px-6 md:px-8 py-3"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderTop: '1px solid var(--theme-card-border)',
          paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
        }}
      >
        <div className="max-w-screen-2xl mx-auto">
          {uploadsInFlight > 0 && (
            <p
              className="flex items-center justify-center gap-1.5 text-[13px] font-medium mb-2"
              style={{ color: 'var(--theme-muted-text)' }}
              data-testid="checklist-submit-pending"
            >
              <Icon name="loader" className="w-4 h-4 animate-spin" />
              {uploadsInFlight === 1
                ? 'Enviando 1 foto — aguarde para concluir.'
                : `Enviando ${uploadsInFlight} fotos — aguarde para concluir.`}
            </p>
          )}
          <Button
            type="button"
            variant="primary"
            icon="send"
            size="xl"
            fullWidth
            // Desabilitado (e não `loading`) enquanto sobem fotos: o spinner de
            // `loading` diria "estou enviando o checklist", que é falso.
            disabled={uploadsInFlight > 0}
            loading={submitting || submitMutation.isPending}
            onClick={() => void handleSubmit()}
            data-testid="checklist-submit"
          >
            Enviar checklist
          </Button>
        </div>
      </div>
    </div>
  );
};

ChecklistFillPage.displayName = 'ChecklistFillPage';
